import AppShell from "../components/layout/AppShell";
import AppFooter from "../components/layout/AppFooter";
import TopNav from "../components/layout/TopNav";
import RemoteSession from '../components/RemoteSession';

import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

export default function TechnicianDashboard({ onLogout, onNavigate }) {
  const [availableTickets, setAvailableTickets] = useState([]);
  const [myTickets, setMyTickets] = useState([]);

  const [cvFile, setCvFile] = useState(null);
  const [cvResult, setCvResult] = useState(null);

  const [validated, setValidated] = useState(false);
  const [technicianId, setTechnicianId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [history, setHistory] = useState([]);
  const [endModal, setEndModal] = useState(null);

  const [activeSession, setActiveSession] = useState(() => {
    const saved = localStorage.getItem('activeSession');
    return saved ? JSON.parse(saved) : null;
  });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchTickets = useCallback(async (techId) => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers: getHeaders() });
      const all = res.data || [];

      setAvailableTickets(all.filter((t) => t.status === "en_attente"));

      setMyTickets(
        all.filter(
          (t) =>
            String(t.technician_id) === String(techId) && t.status === "en_cours"
        )
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchHistory = useCallback(async (techId) => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers: getHeaders() });
      const resolved = res.data.filter(
        t => String(t.technician_id) === String(techId) && (t.status === 'resolu' || t.status === 'en_validation')
      );

      const withReviews = await Promise.all(
        resolved.map(async (t) => {
          try {
            const r = await axios.get(`${API}/reviews/ticket/${t.id}`, { headers: getHeaders() });
            return { ...t, review: r.data };
          } catch {
            return { ...t, review: null };
          }
        })
      );

      setHistory(withReviews);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API}/technicians/me`, {
          headers: getHeaders(),
        });
        setValidated(!!res.data.validated);
        setTechnicianId(res.data.id);
        if (res.data.validated) {
          await fetchTickets(res.data.id);
          await fetchHistory(res.data.id);
        }
      } catch (err) {
        console.error(err);
        setValidated(false);
      } finally {
        setCheckingStatus(false);
      }
    };

    init();
  }, [fetchTickets]);

  // Polling + notifs
  useEffect(() => {
    if (!technicianId || !validated) return;

    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    let prevCount = 0;

    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/tickets`, { headers: getHeaders() });
        const available = res.data.filter(t => t.status === 'en_attente');

        if (available.length > prevCount && prevCount !== 0) {
          if (Notification.permission === 'granted') {
            const hasExpress = available.some(t => t.type === 'express');
            new Notification(hasExpress ? 'Solvya — ⚡ Ticket Express !' : 'Solvya — Nouveau ticket', {
              body: hasExpress ? 'Un ticket Express prioritaire est disponible !' : 'Un nouveau ticket est disponible.',
              icon: '/favicon.ico',
            });
          }
        }
        prevCount = available.length;

        setAvailableTickets(available);
        setMyTickets(
          res.data.filter(
            t => String(t.technician_id) === String(technicianId) && t.status === 'en_cours'
          )
        );
        await fetchHistory(technicianId);
      } catch (err) {
        console.error(err);
      }
    }, 15000);

    return () => clearInterval(poll);
  }, [technicianId, validated]);

  const uploadCV = async (e) => {
    e.preventDefault();
    if (!cvFile) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("cv", cvFile);

    try {
      const res = await axios.post(`${API}/cv/upload`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });

      setCvResult(res.data);

      if (res.data?.score >= 60) {
        setTimeout(() => window.location.reload(), 1200);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const takeTicket = async (ticketId) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}/assign`,
        { technician_id: technicianId },
        { headers: getHeaders() }
      );

      const sessionRes = await axios.post(
        `${API}/sessions/start`,
        { ticket_id: ticketId, technician_id: technicianId },
        { headers: getHeaders() }
      );

      const session = { ...sessionRes.data, ticket_id: ticketId };
      setActiveSession(session);
      localStorage.setItem('activeSession', JSON.stringify(session));
      fetchTickets(technicianId);
    } catch (err) {
      console.error(err);
    }
  };

  const endSession = async (ticketId) => {
    if (!activeSession?.id) {
      alert("Impossible de terminer : session active introuvable (refresh ?).");
      return;
    }

    try {
      const res = await axios.patch(
        `${API}/sessions/${activeSession.id}/end`,
        {},
        { headers: getHeaders() }
      );

      await axios.patch(
        `${API}/tickets/${ticketId}/resolve`,
        {},
        { headers: getHeaders() }
      );

      setEndModal({
        duration: res.data.duration_minutes,
        total: res.data.amount_charged,
        commission: res.data.commission,
        payout: res.data.tech_payout,
        type: res.data.ticket_type,
      });

      setActiveSession(null);
      localStorage.removeItem('activeSession');
      fetchTickets(technicianId);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la fin de session.");
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "en_attente": return "En attente";
      case "en_cours": return "En cours";
      case "resolu": return "Résolu";
      default: return status;
    }
  };

  const statusClass = (status) => {
    if (status === "resolu") return "done";
    if (status === "en_cours") return "progress";
    return "waiting";
  };

  const renderDiagCompact = (ticket) => {
    if (!ticket.diagnostic) return null;
    try {
      const d = typeof ticket.diagnostic === 'string'
        ? JSON.parse(ticket.diagnostic)
        : ticket.diagnostic;
      return (
        <div className="ai-box" style={{ marginTop: 10, fontSize: 12 }}>
          <h4 style={{ marginTop: 0, fontSize: 13 }}>Diagnostic</h4>
          <p><strong>Catégorie :</strong> {d.categorie} · <strong>Urgence :</strong> {d.urgence}</p>
          {d.temps_estime && <p><strong>Temps estimé :</strong> {d.temps_estime}</p>}
          <p><strong>Hypothèses :</strong></p>
          <ul>{d.hypotheses?.slice(0, 3).map((h, i) => <li key={i}>{h}</li>)}</ul>
          <p><strong>Étapes :</strong></p>
          <ul>{d.etapes?.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      );
    } catch { return null; }
  };

  if (checkingStatus) {
    return (
      <AppShell header={<TopNav isLoggedIn={true} onLogout={onLogout} onNavigate={onNavigate} role="technicien" />} footer={<AppFooter />} onNavigate={onNavigate}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <h2 className="page-title">Espace technicien</h2>
          <div className="section-card">
            <div className="muted">Chargement…</div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      header={<TopNav isLoggedIn={true} onLogout={onLogout} onNavigate={onNavigate} role="technicien" />}
      footer={<AppFooter onNavigate={onNavigate} />}
      onNavigate={onNavigate}
    >
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="tech-head">
          <h2 className="page-title">Espace technicien</h2>
          <button className="btn btn-ghost" onClick={onLogout}>
            Déconnexion
          </button>
        </div>

        {!validated ? (
          <div className="section-card">
            <div className="section-head">
              <h3 className="section-title">Déposez votre CV</h3>
            </div>

            <p className="muted" style={{ marginTop: 0 }}>
              Notre IA analysera votre profil. Validation automatique si score ≥ 60/100.
            </p>

            <form onSubmit={uploadCV} className="stack">
              <input
                type="file"
                accept=".pdf"
                className="input"
                onChange={(e) => setCvFile(e.target.files?.[0] || null)}
              />

              <button
                type="submit"
                disabled={loading || !cvFile}
                className="btn btn-primary"
              >
                {loading ? "Analyse en cours..." : "Soumettre mon CV"}
              </button>
            </form>

            {cvResult && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  Résultat de l'analyse IA
                </div>

                <div className="tech-score">
                  <span className="tech-score__value">{cvResult.score}</span>
                  <span className="tech-score__max">/100</span>
                </div>

                <div className="tech-bar">
                  <div
                    className="tech-bar__fill"
                    style={{ width: `${Math.max(0, Math.min(100, cvResult.score || 0))}%` }}
                    data-score={cvResult.score}
                  />
                </div>

                <div className="tech-meta">
                  <div>
                    <span className="muted">Niveau :</span>{" "}
                    <strong>{cvResult.niveau_support}</strong>
                  </div>
                  <div>
                    <span className="muted">Compétences :</span>{" "}
                    <strong>{(cvResult.competences || []).join(", ")}</strong>
                  </div>
                </div>

                {cvResult.red_flags?.length > 0 && (
                  <div className="tech-warn">
                    ⚠️ {cvResult.red_flags.join(", ")}
                  </div>
                )}

                {cvResult.score >= 60 ? (
                  <div className="tech-ok">✓ Score suffisant — validation en cours…</div>
                ) : (
                  <div className="tech-bad">
                    Score insuffisant. Vous pouvez resoumettre un CV amélioré.
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Tickets disponibles */}
            <div className="section-card">
              <div className="section-head">
                <h3 className="section-title">Tickets disponibles</h3>
              </div>

              {availableTickets.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📩</div>
                  <p className="empty-title">Aucun ticket en attente</p>
                  <p className="empty-sub">Reviens dans quelques minutes.</p>
                </div>
              ) : (
                [...availableTickets]
                  .sort((a, b) => (b.type === 'express' ? 1 : 0) - (a.type === 'express' ? 1 : 0))
                  .map((ticket) => (
                    <div key={ticket.id} className="card ticket-card" style={ticket.type === 'express' ? { border: '1px solid rgba(74,222,128,0.25)', background: 'rgba(74,222,128,0.03)' } : {}}>
                      <div className="tech-ticket__row">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <p className="ticket-title" style={{ margin: 0 }} title={ticket.description}>
                              #{ticket.id} — {ticket.description}
                            </p>
                            {ticket.type === 'express' && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80', whiteSpace: 'nowrap' }}>
                                ⚡ EXPRESS
                              </span>
                            )}
                          </div>
                          {renderDiagCompact(ticket)}
                        </div>

                        <div
                          className={`badge ${statusClass("en_attente")}`}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <span className="badge-dot" />
                          <span>{statusLabel("en_attente")}</span>
                        </div>
                      </div>

                      <div className="tech-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => takeTicket(ticket.id)}
                          style={ticket.type === 'express' ? { background: 'rgba(74,222,128,0.18)', borderColor: 'rgba(74,222,128,0.4)', color: '#4ade80' } : {}}
                        >
                          {ticket.type === 'express' ? '⚡ Prendre en charge (Express)' : 'Prendre en charge'}
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Mes tickets en cours */}
            <div className="section-card">
              <div className="section-head">
                <h3 className="section-title">Mes tickets en cours</h3>
              </div>

              {myTickets.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🧰</div>
                  <p className="empty-title">Aucun ticket en cours</p>
                  <p className="empty-sub">Prends un ticket dans la liste au-dessus.</p>
                </div>
              ) : (
                myTickets.map((ticket) => (
                  <div key={ticket.id} className="card ticket-card">
                    <div className="tech-ticket__row">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <p className="ticket-title" style={{ margin: 0 }} title={ticket.description}>
                            #{ticket.id} — {ticket.description}
                          </p>
                          {ticket.type === 'express' && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80', whiteSpace: 'nowrap' }}>
                              ⚡ EXPRESS
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`badge ${statusClass("en_cours")}`} style={{ whiteSpace: "nowrap" }}>
                        <span className="badge-dot" />
                        <span>{statusLabel("en_cours")}</span>
                      </div>
                    </div>

                    {String(activeSession?.ticket_id) === String(ticket.id) ? (
                      <RemoteSession
                        sessionId={ticket.id}
                        role="technicien"
                        onEnd={() => endSession(ticket.id)}
                        ticketType={ticket.type}
                      />
                    ) : (
                      <div className="tech-actions">
                        <button className="btn btn-danger" onClick={() => endSession(ticket.id)}>
                          Terminer la session
                        </button>
                      </div>
                    )}
                    {renderDiagCompact(ticket)}
                  </div>
                ))
              )}
            </div>

            {/* Historique */}
            <div className="section-card">
              <div className="section-head">
                <h3 className="section-title">Historique</h3>
              </div>

              {history.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <p className="empty-title">Aucune intervention terminée</p>
                </div>
              ) : (
                history.map((ticket) => (
                  <div key={ticket.id} className="card ticket-card">
                    <div className="tech-ticket__row">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <p className="ticket-title" style={{ margin: 0 }}>#{ticket.id} — {ticket.description}</p>
                          {ticket.type === 'express' && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80', whiteSpace: 'nowrap' }}>
                              ⚡ EXPRESS
                            </span>
                          )}
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                          {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <div className="badge done">
                        <span className="badge-dot" />
                        <span>Résolu</span>
                      </div>
                    </div>

                    {ticket.review ? (
                      <div style={{ marginTop: 10, borderTop: '1px solid #2a2a2a', paddingTop: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          {[1, 2, 3, 4, 5].map(s => (
                            <span key={s} style={{ color: s <= ticket.review.note ? '#f59e0b' : '#444', fontSize: 16 }}>★</span>
                          ))}
                          <span style={{ fontSize: 12, color: '#666', marginLeft: 4 }}>{ticket.review.note}/5</span>
                          {ticket.review.signalement && (
                            <span style={{ fontSize: 11, color: '#f59e0b', marginLeft: 8 }}>⚠️ Signalement</span>
                          )}
                        </div>
                        {ticket.review.commentaire && (
                          <p style={{ fontSize: 12, color: '#aaa', margin: 0, fontStyle: 'italic' }}>
                            "{ticket.review.commentaire}"
                          </p>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ fontSize: 11, color: '#555' }}>En attente d'évaluation</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
        {endModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 16, padding: 32, maxWidth: 400, width: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <h3 style={{ margin: '0 0 8px', color: '#fff', fontSize: 20 }}>Session terminée</h3>
              <p className="muted" style={{ marginBottom: 20 }}>{endModal.duration} min · {endModal.type === 'express' ? 'Fix Express' : 'À la demande'}</p>

              <div style={{ fontSize: 42, fontWeight: 800, color: '#fff', marginBottom: 4 }}>
                {endModal.total.toFixed(2)} €
              </div>
              <p className="muted" style={{ fontSize: 13, marginBottom: 20 }}>Montant total facturé au client</p>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ padding: '12px 20px', borderRadius: 12, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)' }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>Vous recevez</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#4ade80' }}>{endModal.payout.toFixed(2)} €</div>
                </div>
                <div style={{ padding: '12px 20px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>Commission (20%)</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#888' }}>{endModal.commission.toFixed(2)} €</div>
                </div>
              </div>

              <button className="btn btn-primary" onClick={() => setEndModal(null)} style={{ width: '100%' }}>
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}